import type { Dispatch, ReactNode, SetStateAction } from "react";
import { createContext, use, useState } from "react";

import { LocalStorageKeys } from "@/utils/local-storage-keys";

type CommentState = {
  selfCollapsed: boolean;
  childrenCollapsed: boolean;
};

export type CommentStates = Record<string, CommentState>;

type HoveredLine = {
  postHash: string;
  lineLevel: number;
};

type CommentThreadContextType = {
  commentState: CommentStates;
  setCommentState: Dispatch<SetStateAction<CommentStates>>;
  hoveredLine: HoveredLine | null;
  setHoveredLine: Dispatch<SetStateAction<HoveredLine | null>>;
};

const CommentThreadContext = createContext<CommentThreadContextType>(null!);

const buildInitialCommentState = (postHashes: string[]) => {
  const savedStateString = localStorage.getItem(LocalStorageKeys.commentStates);
  const savedState = savedStateString ? JSON.parse(savedStateString) : {};

  return postHashes.reduce((acc, postHash) => {
    acc[postHash] = {
      selfCollapsed: savedState[postHash]?.selfCollapsed || false,
      childrenCollapsed: savedState[postHash]?.childrenCollapsed || false,
    };
    return acc;
  }, {} as CommentStates);
};

export const CommentThreadContextProvider = ({
  children,
  postHashes,
}: {
  children: ReactNode;
  postHashes: string[];
}) => {
  const [commentState, _setCommentState] = useState<CommentStates>(
    buildInitialCommentState(postHashes),
  );

  const setCommentState = (newState: SetStateAction<CommentStates>) => {
    const savedStateString = localStorage.getItem(
      LocalStorageKeys.commentStates,
    );
    const savedState = savedStateString ? JSON.parse(savedStateString) : {};

    _setCommentState((prevState) => {
      const updatedState = {
        ...prevState,
        ...(typeof newState === "function" ? newState(prevState) : newState),
      };
      const newSavedState = Object.fromEntries(
        Object.entries<CommentStates>({
          ...savedState,
          ...updatedState,
        }).filter(([, value]) => {
          return value.selfCollapsed || value.childrenCollapsed;
        }),
      );
      localStorage.setItem(
        LocalStorageKeys.commentStates,
        JSON.stringify(newSavedState),
      );
      return updatedState;
    });
  };

  const [hoveredLine, setHoveredLine] = useState<HoveredLine | null>(null);

  return (
    <CommentThreadContext.Provider
      value={{
        commentState,
        setCommentState,
        hoveredLine,
        setHoveredLine,
      }}
    >
      {children}
    </CommentThreadContext.Provider>
  );
};

export const useCommentThreadContext = () => {
  return use(CommentThreadContext);
};
